import React from 'react';
import { Helmet } from 'react-helmet-async';
import { seoData, structuredData } from '../data/seoData';

interface SEOHeadProps {
  page: 'home' | 'services' | 'about' | 'contact' | 'testimonials' | 'work';
  customTitle?: string;
  customDescription?: string;
  customKeywords?: string;
}

const SEOHead: React.FC<SEOHeadProps> = ({ 
  page, 
  customTitle, 
  customDescription, 
  customKeywords 
}) => {
  const pageData = seoData[page];
  const title = customTitle || pageData.title;
  const description = customDescription || pageData.description;
  const keywords = customKeywords || pageData.keywords;
  const canonical = pageData.canonical;

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      <meta name="author" content="Laxmi Tyre and Motors" />
      <meta name="robots" content="index, follow" />
      <meta name="language" content="English" />
      <meta name="revisit-after" content="7 days" />
      
      {/* Canonical URL */}
      <link rel="canonical" href={canonical} />
      
      {/* Open Graph Meta Tags */}
      <meta property="og:type" content="business.business" />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonical} />
      <meta property="og:site_name" content={seoData.siteName} />
      <meta property="og:image" content={`${seoData.siteUrl}/og-image.jpg`} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:locale" content="en_IN" />
      
      {/* Business Specific Open Graph */}
      <meta property="business:contact_data:street_address" content="Limbodi" />
      <meta property="business:contact_data:locality" content="Indore" />
      <meta property="business:contact_data:region" content="Madhya Pradesh" />
      <meta property="business:contact_data:postal_code" content="452001" />
      <meta property="business:contact_data:country_name" content="India" />
      <meta property="business:contact_data:phone_number" content={seoData.businessPhone} />
      <meta property="business:contact_data:email" content={seoData.businessEmail} />
      
      {/* Twitter Card Meta Tags */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={`${seoData.siteUrl}/twitter-image.jpg`} />
      <meta name="twitter:site" content="@LaxmiMotors" />
      
      {/* Local Business Meta Tags */}
      <meta name="geo.region" content="IN-MP" />
      <meta name="geo.placename" content="Indore" />
      <meta name="geo.position" content="22.7196;75.8577" />
      <meta name="ICBM" content="22.7196, 75.8577" />
      
      {/* Mobile Meta Tags */}
      <meta name="viewport" content="width=device-width, initial-scale=1.0" />
      <meta name="format-detection" content="telephone=yes" />
      <meta name="apple-mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-status-bar-style" content="black-translucent" />
      
      {/* Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify(structuredData)}
      </script>
      
      {/* Additional Service-specific structured data for services page */}
      {page === 'services' && (
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Service",
            "serviceType": "Automobile Repair Services",
            "provider": {
              "@type": "AutoRepair",
              "name": "Laxmi Tyre and Motors"
            },
            "areaServed": "Indore, Madhya Pradesh",
            "hasOfferCatalog": {
              "@type": "OfferCatalog",
              "name": "Car Services",
              "itemListElement": [
                {
                  "@type": "Offer",
                  "itemOffered": {
                    "@type": "Service",
                    "name": "Car Wash Service",
                    "description": "Exterior washing, interior vacuuming, tyre cleaning, waxing, polishing"
                  },
                  "price": "250",
                  "priceCurrency": "INR"
                },
                {
                  "@type": "Offer",
                  "itemOffered": {
                    "@type": "Service",
                    "name": "Car Denting and Painting Service",
                    "description": "Complete dent repair and painting to restore vehicle body"
                  },
                  "price": "1000",
                  "priceCurrency": "INR"
                }
              ]
            }
          })}
        </script>
      )}
      
      {/* FAQ structured data for home page */}
      {page === 'home' && (
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": [
              {
                "@type": "Question",
                "name": "What services does Laxmi Tyre & Motors offer?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "We offer comprehensive car services including car wash, denting & painting, AC repair, mechanical repairs, battery service, tyre service, and regular maintenance."
                }
              },
              {
                "@type": "Question",
                "name": "Where is Laxmi Tyre & Motors located?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "We are located in Limbodi, Indore, Madhya Pradesh 452001. We have been serving the Indore area since 2011."
                }
              },
              {
                "@type": "Question",
                "name": "What are the working hours?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "We are open Monday to Saturday from 9:00 AM to 7:00 PM, and Sunday from 10:00 AM to 5:00 PM."
                }
              }
            ]
          })}
        </script>
      )}
    </Helmet>
  );
};

export default SEOHead;